import { cartService } from "@/services/cart";
import {
  AddOutcome,
  isOneTimeAvailableLine,
} from "@/services/commerceAddOutcome";
import {
  decodeProgramOffer,
  ProgramItem,
  ProgramOffer,
} from "@/services/commerceProgramOffers";
import {
  parseCompleteCart,
  refusalMessage,
  requoteOffer,
} from "@/services/commerceQuoteAndAdd";
import { AddCartItemPayload, Cart, CartItem } from "@/types/cart";

/**
 * Tech spec 5.4 outcome of a program tap. retryable: nothing was sent. partial: the cart
 * now holds some of the program's lines but not the exact set. The batch names its offer, so
 * the server adds all or nothing (S12); a partial cart means something else changed it.
 * refusal is the server's 400/409 message, as in Phase 1. kept: the decoded names of products
 * an added program left as the cart's existing subscription lines.
 */
type ProgramOutcome = { outcome: AddOutcome; partial?: true; kept?: string[] };
export type ProgramAddResult =
  | { outcome: "retryable" }
  | (ProgramOutcome & { refusal?: string });
/** A tap's outcome; review carries the fresh program for a second tap, as in Phase 1. */
export type ProgramQuoteAndAddResult =
  | { outcome: "review"; offer: ProgramOffer }
  | { outcome: "unavailable" }
  | ProgramAddResult;

/** One program line, built field by field so no extra quote field reaches the cart. */
const buildProgramCartItem = (item: ProgramItem): AddCartItemPayload => ({
  item_type: "bundle-treatment",
  treatment_product_id: item.treatment_product_id,
  quantity: 1,
  is_subscription: false,
});

const itemLines = (cart: Cart, item: ProgramItem): CartItem[] =>
  cart.line_items.filter(
    (line) => line.treatment_product?.id === item.treatment_product_id
  );

// The kind of line S12's batch keeps as it is: it has a selling plan.
const isSubscriptionLine = (line: CartItem): boolean =>
  line.is_subscription && line.selling_plan_id !== null;

/** Products the batch may keep as the cart's subscription line (S12): the ones the quote
 * marked, plus any the add response names. Only the decoded offer's products count. */
const keepableIds = (offer: ProgramOffer, keptSlugs?: unknown): Set<number> =>
  new Set(
    offer.products
      .filter(
        (product) =>
          product.in_cart_subscription ||
          (Array.isArray(keptSlugs) && keptSlugs.includes(product.product_slug))
      )
      .map((product) => product.treatment_product_id)
  );

/**
 * Whether the add response (or readback) holds the exact set: every line a new one-time
 * bundle line, or a keepable product's subscription line left as it is, in the same active
 * cart. One line never proves the set. Products the quote left out as subscribed are not
 * in items, so their absence is expected.
 *
 * Pre: cartBefore is an authoritative read holding no unkeepable item's treatment.
 */
function decideProgramOutcome(
  offer: ProgramOffer,
  keepable: Set<number>,
  cartBefore: Cart,
  cartAfter: Cart | undefined
): ProgramOutcome {
  if (cartAfter?.id !== cartBefore.id || cartAfter.status !== "active")
    return { outcome: "unconfirmed" };
  const items = offer.action.items;
  const lines = items.map((item) => itemLines(cartAfter, item));
  // Several lines for one treatment are outside the server's contract; claim nothing.
  if (lines.some((found) => found.length > 1))
    return { outcome: "unconfirmed" };
  const keptIds = items
    .filter(
      (item, i) =>
        keepable.has(item.treatment_product_id) &&
        lines[i].some(isSubscriptionLine)
    )
    .map((item) => item.treatment_product_id);
  const landed = lines
    .filter((_, i) => !keptIds.includes(items[i].treatment_product_id))
    .flat();
  if (
    keptIds.length + landed.length === items.length &&
    landed.every((line) => isOneTimeAvailableLine(line, "bundle-treatment"))
  ) {
    const kept = offer.products
      .filter((product) => keptIds.includes(product.treatment_product_id))
      .map((product) => product.name);
    return kept.length ? { outcome: "added", kept } : { outcome: "added" };
  }
  return landed.length > 0
    ? { outcome: "not_added", partial: true }
    : { outcome: "not_added" };
}

/**
 * Add the program's quoted set in one cart batch and confirm the exact set. offer is a
 * fresh quote and quotedCartId the cart it was evaluated against (null: no cart yet).
 * When the add response cannot decide, a strict read of the same cart does. Nothing is
 * ever resubmitted: a retry is a new call, so it re-reads the cart before any add.
 */
export async function addProgram(
  offer: ProgramOffer,
  quotedCartId: string | null
): Promise<ProgramAddResult> {
  const items = offer.action.items;
  const marked = keepableIds(offer);
  // Authoritative read just before the add; nothing has been submitted if it fails.
  const cartBefore = await cartService
    .getOrCreateCart(parseCompleteCart)
    .catch(() => undefined);
  if (!cartBefore) return { outcome: "retryable" };
  // The quote was evaluated against its own cart; a different one needs a fresh quote.
  if (quotedCartId !== null && quotedCartId !== cartBefore.id)
    return { outcome: "retryable" };
  const before = (item: ProgramItem) => itemLines(cartBefore, item);
  // Adding would overwrite an existing line's mode, so never mutate one from chat.
  if (
    items.some(
      (item) => !marked.has(item.treatment_product_id) && before(item).length
    )
  )
    return { outcome: "view_cart" };
  // The batch keeps a marked product's one subscription line as it is (S12). Anything else
  // means the cart changed after the quote: send nothing, so a retry re-quotes.
  const isKeptLine = (found: CartItem[]) =>
    found.length === 1 && isSubscriptionLine(found[0]);
  if (
    items.some(
      (item) =>
        marked.has(item.treatment_product_id) && !isKeptLine(before(item))
    )
  )
    return { outcome: "retryable" };

  let cartAfter: Cart | undefined;
  let refusal: string | undefined;
  try {
    cartAfter = await cartService.addItemsToCart(
      cartBefore.id,
      items.map(buildProgramCartItem),
      parseCompleteCart,
      offer.offer_id
    );
  } catch (error) {
    refusal = refusalMessage(error);
  }
  // A program batch's response names the products it kept as subscription lines (S12).
  const keptSlugs = (
    cartAfter as { kept_subscription_slugs?: unknown } | undefined
  )?.kept_subscription_slugs;
  const keepable = keepableIds(offer, keptSlugs);
  const decided = decideProgramOutcome(offer, keepable, cartBefore, cartAfter);
  if (decided.outcome !== "unconfirmed") return decided;

  // Lost, refused or unreadable response: a failed or changed readback stays unconfirmed.
  const readback = await cartService
    .fetchCart(cartBefore.id, parseCompleteCart)
    .catch(() => undefined);
  const recovered = decideProgramOutcome(offer, marked, cartBefore, readback);
  return recovered.outcome !== "added" && refusal
    ? { ...recovered, refusal }
    : recovered;
}

// Every field the add depends on; names, descriptions, images and perks may change freely.
const programQuoteKey = (offer: ProgramOffer): string =>
  JSON.stringify([
    offer.price,
    offer.kind,
    offer.purchase_mode,
    offer.action.type,
    offer.action.items.map(buildProgramCartItem),
    [...keepableIds(offer)],
  ]);

/**
 * Re-quote the tapped program and add it only when the fresh quote is unchanged, as
 * quoteAndAdd does for one treatment. A changed, withdrawn or in-cart program is answered
 * without touching the cart.
 */
export async function quoteAndAddProgram(
  tapped: ProgramOffer
): Promise<ProgramQuoteAndAddResult> {
  const quote = await requoteOffer(tapped.offer_id, decodeProgramOffer).catch(
    () => "failed" as const
  );
  if (quote === "failed") return { outcome: "retryable" };
  if (quote === "in_cart") return { outcome: "view_cart" };
  if (!quote) return { outcome: "unavailable" };
  if (programQuoteKey(quote.offer) !== programQuoteKey(tapped))
    return { outcome: "review", offer: quote.offer };
  return addProgram(quote.offer, quote.cartId);
}
